import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export interface NavigationHistoryEntry {
  path: string;
  visitedAt: string;
}

const HISTORY_PREFIX = 'intouch-navigation-history:';
const HISTORY_UPDATED_EVENT = 'intouch:navigation-history-updated';
const EXCLUDED_PATHS = new Set(['/', '/auth/login', '/auth/register']);

function historyKey(userId: string): string {
  return `${HISTORY_PREFIX}${userId}`;
}

function readHistory(userId?: string): NavigationHistoryEntry[] {
  if (!userId || typeof window === 'undefined') return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(historyKey(userId)) || '[]');
    return Array.isArray(parsed)
      ? parsed.filter((entry): entry is NavigationHistoryEntry => (
          typeof entry?.path === 'string' && typeof entry?.visitedAt === 'string'
        ))
      : [];
  } catch {
    return [];
  }
}

export function useNavigationHistoryTracker(userId?: string): void {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!userId || EXCLUDED_PATHS.has(pathname)) return;

    const nextEntry = { path: pathname, visitedAt: new Date().toISOString() };
    const history = readHistory(userId).filter((entry) => entry.path !== pathname);
    const nextHistory = [nextEntry, ...history].slice(0, 20);
    window.localStorage.setItem(historyKey(userId), JSON.stringify(nextHistory));
    window.dispatchEvent(new CustomEvent(HISTORY_UPDATED_EVENT, { detail: { userId } }));
  }, [pathname, userId]);
}

export function useNavigationHistory(userId?: string): NavigationHistoryEntry[] {
  const [history, setHistory] = useState<NavigationHistoryEntry[]>(() => readHistory(userId));

  useEffect(() => {
    setHistory(readHistory(userId));
    if (!userId) return;

    const handleUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ userId?: string }>).detail;
      if (!detail?.userId || detail.userId === userId) {
        setHistory(readHistory(userId));
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === historyKey(userId)) setHistory(readHistory(userId));
    };

    window.addEventListener(HISTORY_UPDATED_EVENT, handleUpdate);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(HISTORY_UPDATED_EVENT, handleUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, [userId]);

  return history;
}
